import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { ArrowLeft, Lock, AlertTriangle, MessageSquare, BellOff, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function offSummary(data) {
  if (!data) return null;
  const off = (data.toggles || []).filter(t => !t.enabled);
  const protectedOff = (data.protected || []).filter(t => !t.enabled);
  if (data.master_kill || off.length > 0 || protectedOff.length > 0) {
    return { masterKill: !!data.master_kill, off, protectedOff };
  }
  return null;
}

export function SmsOffBanner({ token, onOpenSettings }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/sms-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setData(res.data))
      .catch(() => {});
  }, [token]);
  const summary = offSummary(data);
  if (!summary) return null;
  const danger = summary.masterKill || summary.protectedOff.length > 0;
  return (
    <div
      className={`rounded-lg border p-3 ${danger ? "bg-red-50 border-red-300" : "bg-amber-50 border-amber-300"}`}
      data-testid="sms-off-banner"
    >
      <div className="flex items-start gap-2">
        <BellOff className={`w-4 h-4 mt-0.5 shrink-0 ${danger ? "text-red-600" : "text-amber-600"}`} />
        <div className="flex-1 text-xs">
          {summary.protectedOff.length > 0 && (
            <div data-testid="sms-protected-off-banner">
              <p className="font-semibold text-red-800">
                ⚠ {summary.protectedOff.length} PROTECTED safety categor{summary.protectedOff.length === 1 ? "y is" : "ies are"} OFF
              </p>
              <ul className="mt-1 space-y-0.5 text-[11px] text-red-700">
                {summary.protectedOff.map(t => (
                  <li key={t.category}>
                    • {t.label}
                    {t.changed_by ? ` — turned off by ${t.changed_by}${t.changed_at ? ` at ${t.changed_at}` : ""}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {summary.masterKill && (
            <p className="font-semibold text-red-800 mt-1">SMS MASTER KILL is ON — all ordinary SMS categories are silenced.</p>
          )}
          {!summary.masterKill && summary.off.length > 0 && (
            <>
              <p className={`font-semibold text-amber-800 ${summary.protectedOff.length > 0 ? "mt-1.5" : ""}`}>
                {summary.off.length} SMS categor{summary.off.length === 1 ? "y is" : "ies are"} currently OFF
              </p>
              <ul className="mt-1 space-y-0.5 text-[11px] text-slate-700">
                {summary.off.map(t => (
                  <li key={t.category}>
                    • {t.label}
                    {t.changed_by ? ` — turned off by ${t.changed_by}${t.changed_at ? ` at ${t.changed_at}` : ""}` : ""}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        {onOpenSettings && (
          <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={onOpenSettings} data-testid="sms-off-banner-open-settings">
            SMS Settings
          </Button>
        )}
      </div>
    </div>
  );
}

export default function SmsSettingsPanel({ token, onBack }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState({});
  const [killConfirm, setKillConfirm] = useState(false);
  // Pending protected change awaiting passcode: {category, label, enabled}
  const [pendingProtected, setPendingProtected] = useState(null);
  const [passcode, setPasscode] = useState("");
  const [passcodeError, setPasscodeError] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    axios.get(`${API}/admin/sms-settings`, { headers })
      .then(res => setData(res.data))
      .catch(() => toast.error("Failed to load SMS settings"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const save = async (body, key) => {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      const res = await axios.put(`${API}/admin/sms-settings`, body, { headers });
      setData(res.data);
      toast.success("Saved — takes effect immediately");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
    setSaving(s => ({ ...s, [key]: false }));
  };

  const submitProtected = async () => {
    if (!pendingProtected) return;
    const { category, enabled } = pendingProtected;
    setPasscodeError("");
    setSaving(s => ({ ...s, [category]: true }));
    try {
      const res = await axios.put(`${API}/admin/sms-settings`, { category, enabled, passcode }, { headers });
      setData(res.data);
      toast.success("Saved — takes effect immediately");
      setPendingProtected(null);
      setPasscode("");
    } catch (e) {
      setPasscodeError(e.response?.data?.detail || "Failed to save");
    }
    setSaving(s => ({ ...s, [category]: false }));
  };

  const closePasscodeDialog = () => {
    setPendingProtected(null);
    setPasscode("");
    setPasscodeError("");
  };

  const summary = offSummary(data);
  const passcodeConfigured = data?.passcode_configured !== false;

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4" data-testid="sms-settings-panel">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="sms-settings-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-orange-600" />
            SMS Settings
          </h2>
          <p className="text-xs text-muted-foreground">
            Turn SMS categories on or off instantly — no redeploy needed. Changes take effect within a minute.
          </p>
        </div>
      </div>

      {summary && (
        <div
          className={`rounded-lg border p-3 text-xs ${summary.masterKill || summary.protectedOff.length > 0 ? "bg-red-50 border-red-300" : "bg-amber-50 border-amber-300"}`}
          data-testid="sms-settings-off-banner"
        >
          {summary.protectedOff.length > 0 && (
            <div className="mb-1.5">
              <p className="font-semibold text-red-800">⚠ PROTECTED safety categories OFF:</p>
              <ul className="mt-1 space-y-0.5 text-red-700">
                {summary.protectedOff.map(t => (
                  <li key={t.category}>• {t.label}{t.changed_by ? ` — by ${t.changed_by}${t.changed_at ? `, ${t.changed_at}` : ""}` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          {summary.masterKill && (
            <p className="font-semibold text-red-800">MASTER KILL is ON — all ordinary categories are silenced. Protected safety categories are NOT affected.</p>
          )}
          {!summary.masterKill && summary.off.length > 0 && (
            <>
              <p className="font-semibold text-amber-800">{summary.off.length} ordinary categor{summary.off.length === 1 ? "y is" : "ies are"} OFF:</p>
              <ul className="mt-1 space-y-0.5 text-slate-700">
                {summary.off.map(t => (
                  <li key={t.category}>• {t.label}{t.changed_by ? ` — by ${t.changed_by}${t.changed_at ? `, ${t.changed_at}` : ""}` : ""}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Master kill */}
      <Card className="border-red-300 bg-red-50/50">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-900">Master Kill Switch</p>
              <p className="text-[11px] text-red-700">
                Silences ALL ordinary categories at once. Protected safety categories below are never affected by this switch.
              </p>
              {data?.master_kill_changed_by && (
                <p className="text-[10px] text-red-600 mt-0.5">
                  Last changed by {data.master_kill_changed_by}{data.master_kill_changed_at ? ` at ${data.master_kill_changed_at}` : ""}
                </p>
              )}
            </div>
            {data?.master_kill ? (
              <Button
                size="sm"
                variant="outline"
                className="border-red-400 text-red-700"
                disabled={!!saving.master_kill}
                onClick={() => save({ master_kill: false }, "master_kill")}
                data-testid="sms-master-kill-off-btn"
              >
                Turn OFF
              </Button>
            ) : killConfirm ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                  disabled={!!saving.master_kill}
                  onClick={() => { setKillConfirm(false); save({ master_kill: true }, "master_kill"); }}
                  data-testid="sms-master-kill-confirm"
                >
                  Confirm — silence all
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setKillConfirm(false)} data-testid="sms-master-kill-cancel">
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="border-red-400 text-red-700"
                onClick={() => setKillConfirm(true)}
                data-testid="sms-master-kill-arm"
              >
                Activate Master Kill
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ordinary toggleable categories */}
      <Card>
        <CardContent className="pt-4 pb-2 divide-y">
          {(data?.toggles || []).map(t => (
            <div key={t.category} className="flex items-center gap-3 py-3" data-testid={`sms-toggle-row-${t.category}`}>
              <div className="flex-1">
                <p className="text-sm font-medium">{t.label}</p>
                {t.changed_by && (
                  <p className="text-[10px] text-muted-foreground">
                    Changed by {t.changed_by}{t.changed_at ? ` at ${t.changed_at}` : ""}
                  </p>
                )}
              </div>
              <span className={`text-[10px] font-semibold ${t.enabled && !data?.master_kill ? "text-green-600" : "text-red-500"}`}>
                {data?.master_kill ? "KILLED" : t.enabled ? "ON" : "OFF"}
              </span>
              <Switch
                checked={t.enabled}
                disabled={!!saving[t.category]}
                onCheckedChange={(checked) => save({ category: t.category, enabled: checked }, t.category)}
                data-testid={`sms-toggle-${t.category}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Protected safety categories — passcode required */}
      <Card className="border-violet-300 bg-violet-50/40">
        <CardContent className="pt-4 pb-2">
          <div className="flex items-center gap-2 pb-2 border-b">
            <ShieldAlert className="w-4 h-4 text-violet-700" />
            <p className="text-sm font-semibold text-violet-900">Protected safety categories</p>
            <span className="text-[10px] text-violet-700 bg-violet-100 border border-violet-200 rounded-full px-2 py-0.5 font-semibold">
              Passcode required
            </span>
          </div>
          {!passcodeConfigured && (
            <p className="text-[11px] text-red-700 font-semibold py-2" data-testid="sms-passcode-not-configured">
              Locked — the safety passcode is not configured on the server. All protected categories are forced ON and cannot be changed.
            </p>
          )}
          <div className="divide-y">
            {(data?.protected || []).map(t => (
              <div key={t.category} className="flex items-center gap-3 py-3" data-testid={`sms-protected-row-${t.category}`}>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{t.label}</p>
                  <p className="text-[10px] text-violet-600">Not affected by the master kill switch</p>
                  {t.changed_by && (
                    <p className="text-[10px] text-muted-foreground">
                      Changed by {t.changed_by}{t.changed_at ? ` at ${t.changed_at}` : ""}
                    </p>
                  )}
                </div>
                <span className={`text-[10px] font-semibold ${t.enabled ? "text-green-600" : "text-red-500"}`}>
                  {t.enabled ? "ON" : "OFF"}
                </span>
                {passcodeConfigured ? (
                  <Switch
                    checked={t.enabled}
                    disabled={!!saving[t.category]}
                    onCheckedChange={(checked) => {
                      setPasscodeError("");
                      setPasscode("");
                      setPendingProtected({ category: t.category, label: t.label, enabled: checked });
                    }}
                    data-testid={`sms-protected-toggle-${t.category}`}
                  />
                ) : (
                  <Lock className="w-4 h-4 text-slate-400" data-testid={`sms-protected-locked-${t.category}`} />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Passcode dialog */}
      <Dialog open={!!pendingProtected} onOpenChange={(open) => { if (!open) closePasscodeDialog(); }}>
        <DialogContent className="max-w-sm" data-testid="sms-passcode-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <ShieldAlert className="w-4 h-4 text-violet-700" />
              Passcode required
            </DialogTitle>
            <DialogDescription className="text-xs">
              Turning {pendingProtected?.enabled ? "ON" : "OFF"}: <span className="font-semibold">{pendingProtected?.label}</span>
            </DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            autoComplete="off"
            placeholder="Enter safety passcode"
            value={passcode}
            onChange={(e) => { setPasscode(e.target.value); setPasscodeError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter" && passcode) submitProtected(); }}
            data-testid="sms-passcode-input"
          />
          {passcodeError && (
            <p className="text-xs text-red-600 font-semibold" data-testid="sms-passcode-error">{passcodeError}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={closePasscodeDialog} data-testid="sms-passcode-cancel">
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-violet-700 hover:bg-violet-800"
              disabled={!passcode || !!saving[pendingProtected?.category]}
              onClick={submitProtected}
              data-testid="sms-passcode-submit"
            >
              Confirm change
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
